import { GetDynamicApi, UpdateDynamicApi } from '@/services/DynamicApi';
import { AsyncThunkConfig } from '@/store';
import { GetAllDistinctValuesFromThisArrayOfObjects } from '@/utils/array-methods';
import { handleGetNestedProperty } from '@/utils/object-methods';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

const initialState: DynamicKanbanSlice = {
  loading: false,
  updatingItemId: null,
  data: null,
  headerSortingData: ['All'],
  board: {},
  sorted: [],
  filterDate: { start: dayjs().subtract(1, 'week').toDate(), end: new Date() },
};

const dynamicKanbanSlice = createSlice({
  name: 'dynamicKanban',
  initialState: initialState,
  reducers: {
    updateDynamicKanbanSortedDataReducer: (state, action: PayloadAction<DynamicKanbanSlice['sorted']>) => {
      state.sorted = action.payload;
    },
    updateDynamicKanbanBoardDataReducer: (state, action: PayloadAction<DynamicKanbanSlice['board']>) => {
      state.board = action.payload;
    },
    updateDynamicKanbanUpdatingItemIdDataReducer: (state, action: PayloadAction<DynamicKanbanSlice['updatingItemId']>) => {
      state.updatingItemId = action.payload;
    },
    updateDynamicKanbanFilterDateReducer: (state, action: PayloadAction<DynamicKanbanSlice['filterDate']>) => {
      state.filterDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDynamicKanbanDataThunk.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getDynamicKanbanDataThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload.data;
      state.headerSortingData = action.payload.headerSortingData;
      state.sorted = action.payload.sorted;
      state.board = action.payload.board;
    });
    builder.addCase(updateDynamicKanbanBoardDataThunk.fulfilled, (state, action) => {
      if (action.payload === null) return;
      state.sorted = action.payload.sorted;
      state.board = action.payload.board;
    });
  },
});

export const { updateDynamicKanbanSortedDataReducer, updateDynamicKanbanBoardDataReducer, updateDynamicKanbanUpdatingItemIdDataReducer, updateDynamicKanbanFilterDateReducer } = dynamicKanbanSlice.actions;
export default dynamicKanbanSlice.reducer;

export interface DynamicKanbanSlice {
  loading: boolean;
  updatingItemId: string | null;
  headerSortingData: string[];
  data: Record<string, unknown>[] | null;
  board: Record<string, Record<string, unknown>[]>;
  sorted: { _id: string; title: string; items: Record<string, unknown>[]; id?: string }[];
  filterDate: { start: Date; end: Date };
}

// ASYNC THUNKS
export const getDynamicKanbanDataThunk = createAsyncThunk('dynamicKanban/getDynamicKanbanDataThunk', async ({ collection, sectionField, sortField, filter, baseUrl, limit, skip }: { collection: string; sectionField: string; sortField: string; filter: Record<string, unknown>; limit?: number; skip?: number; baseUrl?: string }) => {
  const response = await GetDynamicApi({ collection, filter, limit, skip }, baseUrl);
  const rawData = response.data || [];
  const columnDistinctValues = GetAllDistinctValuesFromThisArrayOfObjects(rawData, sectionField) as string[];
  const kanbanDataMap = new Map<string, Record<string, unknown>[]>();
  rawData.forEach((item) => {
    const key = handleGetNestedProperty(item, sectionField) as string;
    const dataEntry = kanbanDataMap.get(key);
    if (dataEntry === undefined) kanbanDataMap.set(key, [item]);
    else dataEntry.push(item);
  });

  return {
    data: rawData,
    board: Object.fromEntries(kanbanDataMap.entries()),
    headerSortingData: ['All', ...GetAllDistinctValuesFromThisArrayOfObjects(rawData, sortField)] as string[],
    sorted: columnDistinctValues.map((item) => ({ _id: item, title: item, items: kanbanDataMap.get(item) || [] })),
  };
});

// HANDLE UPDATE FOR NESTED FIELDS
export const updateDynamicKanbanBoardDataThunk = createAsyncThunk<Pick<DynamicKanbanSlice, 'sorted' | 'board'> | null, { collection: string; sectionField: string; board: DynamicKanbanSlice['board']; item: Record<string, unknown>; targetFieldValue: string }, AsyncThunkConfig>('dynamicKanban/updateDynamicKanbanBoardDataThunk', async ({ collection, sectionField, board, item, targetFieldValue }, { dispatch, getState }) => {
  const currentState = getState().dynamicKanban;

  const itemId = item['_id'] as string;

  let processedSectionField = sectionField;
  let processedTargetFieldValue = targetFieldValue;

  const hasDotNotation = sectionField.includes('.');
  if (hasDotNotation && currentState.data !== null) {
    processedSectionField = sectionField.split('.')[0];
    const distinctObjectVal = GetAllDistinctValuesFromThisArrayOfObjects(currentState.data, processedSectionField);
    processedTargetFieldValue = distinctObjectVal.find((item) => item[sectionField.split('.')[1]] === processedTargetFieldValue) || processedTargetFieldValue;
  }

  dispatch(updateDynamicKanbanBoardDataReducer(board));
  dispatch(updateDynamicKanbanUpdatingItemIdDataReducer(itemId));

  const response = await UpdateDynamicApi({ collection, filter: { _id: itemId }, data: { [processedSectionField]: processedTargetFieldValue } });
  dispatch(updateDynamicKanbanUpdatingItemIdDataReducer(null));

  // updation failed, revert changes here
  if (response.data.status !== 200) {
    dispatch(updateDynamicKanbanBoardDataReducer(currentState.board));
    return null;
  }

  const updatedState = getState().dynamicKanban;

  // updation success, update changes here
  const updatedBoard = { ...updatedState.board };
  Object.keys(updatedBoard).forEach((section) => {
    if (section === targetFieldValue) {
      const updated = updatedBoard[section].map((item) => {
        if (item['_id'] === itemId) return { ...item, [sectionField]: targetFieldValue };
        return item;
      });
      updatedBoard[section] = updated;
    }
  });

  const updatedSorted = updatedState.sorted.map((section) => {
    if (item.title === targetFieldValue)
      section.items = section.items.map((item) => {
        if (item['_id'] === itemId) item[sectionField] = targetFieldValue;
        return item;
      });
    return section;
  });

  return {
    board: updatedBoard,
    sorted: updatedSorted,
  };
});
